import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Pagination from "@mui/material/Pagination";

// Component Imports
import Navbar from "../../components/Navbar/Navbar";
import Topbar from "../../components/Topbar/Topbar";
import HorizontalVoyageCard from "../../components/HorizontalVoyageCard/HorizontalVoyageCard";
import Footer from "../../components/Footer/Footer";
import { useTranslation } from 'react-i18next';
// API and Styling
import API from "../../API";
import "./voyage.css";

export default function Voyage() {
  const { t } = useTranslation();
  const [voyages, setVoyages] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalVoyages: 0,
    pageSize: 5,
  });

  const [searchParams, setSearchParams] = useState({
    departureDate: null,
    arrivalDate: null,
  });

  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [committedPriceRange, setCommittedPriceRange] = useState([0, 500000]);
  const [debouncedSearchParams, setDebouncedSearchParams] = useState(searchParams);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchParams(searchParams);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchParams]);

  const fetchVoyages = useCallback(async (page = 1) => {
    try {
      const response = await API.get("/voyage-organize/user/show", {
        params: {
          page,
          limit: 5,
          departureDate: searchParams.departureDate
            ? format(searchParams.departureDate, 'yyyy-MM-dd')
            : undefined,
          arrivalDate: searchParams.arrivalDate
            ? format(searchParams.arrivalDate, 'yyyy-MM-dd')
            : undefined,
          priceMin: committedPriceRange[0] || undefined,
          priceMax: committedPriceRange[1] || undefined,
        },
      });

      const paginationData = response.data.pagination;
      setPagination({
        currentPage: parseInt(paginationData.currentPage),
        totalPages: parseInt(paginationData.totalPages),
        totalVoyages: parseInt(paginationData.totalVoyages),
        pageSize: parseInt(paginationData.pageSize),
      });

      setVoyages(response.data.voyages);
    } catch (error) {
      console.error("Error fetching data:", error);
      setVoyages([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalVoyages: 0,
        pageSize: 5,
      });
    }
  }, [debouncedSearchParams, committedPriceRange]);

  useEffect(() => {
    fetchVoyages(1);
  }, [debouncedSearchParams, committedPriceRange, fetchVoyages]);

  const handleDateChange = (field, date) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const resetFilters = () => {
    setSearchParams({
      departureDate: null,
      arrivalDate: null,
    });
    setPriceRange([0, 500000]);
    setCommittedPriceRange([0, 500000]);
    fetchVoyages(1);
  };

  const parseDepartures = (departures) => {
    if (Array.isArray(departures)) return departures;
    if (typeof departures === 'string') {
      try { return JSON.parse(departures); } catch { return []; }
    }
    return [];
  };

  const calculateDuration = (departures) => {
    const parsed = parseDepartures(departures);
    if (!parsed || parsed.length === 0) return "";
    const startDate = new Date(parsed[0].start);
    const endDate = new Date(parsed[0].end);
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const nights = totalDays - 1;
    return `${totalDays} jours, ${nights} nuits`;
  };

  const handlePageChange = (event, value) => {
    fetchVoyages(value);
  };

  const filterVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 100,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        type: "spring",
        stiffness: 120,
      },
    },
  };

  return (
    <div className="Page_voyage">
      <Topbar />
      <Navbar page={"voyage"} />

      <div className="voyage-container">
        {/* Mobile Filter Toggle (inline, above filters/results) */}
        <button
          className="voyage-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
        >
          {mobileFiltersOpen ? <FaTimes /> : <FaFilter />}
          {mobileFiltersOpen ? t("voyage.search.close_filters") : t("voyage.search.open_filters")}
        </button>
        {/* Filters Sidebar */}
        <motion.div
          className={`voyage-filters-section ${mobileFiltersOpen ? 'mobile-open' : ''}`}
          initial="hidden"
          animate="visible"
          variants={filterVariants}
        >
          <div className="filters-header">
            <h3>{t("voyage.search.filters")}</h3>
          </div>

          <div className="filter-group">
            <label>{t("voyage.search.departure_date_label")}</label>
            <DatePicker
              selected={searchParams.departureDate}
              onChange={(date) => handleDateChange('departureDate', date)}
              placeholderText={t("voyage.search.departure_date_placeholder")}
              className="filter-input"
              locale={fr}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
            />
          </div>

          <div className="filter-group">
            <label>{t("voyage.search.arrival_date_label")}</label>
            <DatePicker
              selected={searchParams.arrivalDate}
              onChange={(date) => handleDateChange('arrivalDate', date)}
              placeholderText={t("voyage.search.arrival_date_placeholder")}
              className="filter-input"
              locale={fr}
              dateFormat="dd/MM/yyyy"
              minDate={searchParams.departureDate || new Date()}
            />
          </div>

          <div className="filter-group">
            <label>{t("voyage.search.budget_label")}</label>
            <Box sx={{ width: "100%" }}>
              <Slider
                min={0}
                max={500000}
                step={5000}
                value={priceRange}
                onChange={handlePriceChange}
                onChangeCommitted={(event, newValue) => setCommittedPriceRange(newValue)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v.toLocaleString()} DA`}
                className="price-slider"
              />
            </Box>
            <div className="price-range-display">
              <span>{priceRange[0].toLocaleString()} DA</span>
              <span>-</span>
              <span>{priceRange[1].toLocaleString()} DA</span>
            </div>
          </div>

          <button
            className="voyage-search-button"
            onClick={() => fetchVoyages(1)}
          >
            <FaSearch /> {t("voyage.search.search_button")}
          </button>
          <button
            className="voyage-reset-button"
            onClick={resetFilters}
          >
            {t("voyage.search.reset_button")}
          </button>
        </motion.div>

        {/* Results Section */}
        <div className="voyage-results-section">
          <h2 className="voyage-results-title">{t("voyage.title")}</h2>

          <AnimatePresence>
            {voyages.length > 0 ? (
              <div className="voyage-list">
                {voyages.map((voyage) => (
                  <motion.div
                    key={voyage.id}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <HorizontalVoyageCard
                      voyage={voyage}
                      duration={calculateDuration(voyage.departures)}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="voyage-no-results">
                <h3>{t("voyage.no_results")}</h3>
                <p>{t("voyage.try_different_filters")}</p>
              </div>
            )}
          </AnimatePresence>

          {voyages.length > 0 && pagination.totalPages > 1 && (
            <div className="voyage-pagination">
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}